import { z } from 'zod';

/**
 * GET /api/flow/queue/items/:id/recent-sends — the item contact's merged
 * 30-day send history (Warm Intro plan U10, R5).
 *
 * R5 is "warn, never block": this feed backs the item editor's recency bar
 * and its history accordion (U11) and is informational only — like
 * `WarningsResponseSchema`, nothing here may gate mark-ready or send.
 *
 * Two sources merge into one list:
 *   - `playhouse` — an invite the Playhouse app recorded
 *     (`salesforce_campaign_member` ∪ `launcher_invitee_actions`);
 *     labeled by campaign, no `slug` (Playhouse has no play types).
 *   - `queue` — a delivered Flow queue send (`action_executions` for a play);
 *     labeled by the play (warm intro rows name the connector), `slug` set to
 *     the play type so the UI can render the prototype's `Queue · {slug}` tag.
 */
export const RecentSendSchema = z.object({
  /** What the rep sees in the history table's "Send" column. */
  label: z.string(),
  source: z.enum(['playhouse', 'queue']),
  /**
   * Play type slug — present on `queue` rows only (a Playhouse invite has no
   * play type). Optional rather than nullable so a Playhouse row simply omits
   * it, matching the prototype's `s.slug` check.
   */
  slug: z.string().optional(),
  /** ISO-8601 timestamp of the send — `created_at` convention, wire = string. */
  occurred_at: z.string(),
  /**
   * The send's status as the rep should read it: the SFDC campaign-member
   * status ('Registered', 'Invited', …), the literal `Invite sent` for a
   * Terra-side Playhouse invite, or `Sent` for a delivered queue send.
   *
   * NULLABLE on purpose: `salesforce_campaign_member.status` is
   * nullable in the mirror, and inventing a status for such a row would be a
   * fabricated fallback. The UI renders no status pill instead.
   */
  status: z.string().nullable(),
}).strict();
export type RecentSend = z.infer<typeof RecentSendSchema>;

/**
 * The response is a BARE array (not `{ sends: [...] }`) — same shape choice
 * as its sibling `/warnings`, and what the bar's "no sends in 30 days -> no
 * bar" render reduces to (`length === 0`). Date-descending, newest first.
 */
export const RecentSendsResponseSchema = z.array(RecentSendSchema);
export type RecentSendsResponse = z.infer<typeof RecentSendsResponseSchema>;
